import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ProjectChangeHistoryPage } from './types';

export function projectHistoryQueryKey(projectId: string, columnId?: string) {
  return columnId
    ? (['projectHistory', projectId, columnId] as const)
    : (['projectHistory', projectId] as const);
}

export function useProjectChangeHistory(
  projectId: string,
  opts: { columnId?: string; enabled?: boolean }
) {
  return useQuery({
    queryKey: projectHistoryQueryKey(projectId, opts.columnId),
    queryFn: () => api.projects.getChanges(projectId, { columnId: opts.columnId }),
    enabled: (opts.enabled ?? true) && !!projectId,
    staleTime: 30_000,
  });
}

export function invalidateProjectHistory(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
  columnId?: string
) {
  queryClient.invalidateQueries({ queryKey: ['projectHistory', projectId] });
  if (columnId) {
    queryClient.invalidateQueries({ queryKey: projectHistoryQueryKey(projectId, columnId) });
  }
}

export function mergeHistoryPage(
  prev: ProjectChangeHistoryPage | undefined,
  next: ProjectChangeHistoryPage
): ProjectChangeHistoryPage {
  if (!prev) return next;
  const seen = new Set(prev.items.map((i) => i.id));
  const merged = [...prev.items];
  for (const item of next.items) {
    if (!seen.has(item.id)) merged.push(item);
  }
  return { items: merged, next_cursor: next.next_cursor };
}
